import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  LAB_ATTACHMENT_IN_LABPORTALEN_LABEL,
  type ClinicianLabOrder,
  type LabAnalyteMappingList,
  type LabPortalenQuarantineItem,
  type LabPortalenStatus,
  type PatientListRow,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { useReadOnly } from "@/components/SupportView";
import {
  fetchPatient,
  fetchPatients,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  analysisValueText,
  attachableOrders,
  canFetchLabPortalenNow,
  canManageMappings,
  canResolveQuarantine,
  canRerunQuarantine,
  fetchNowLabel,
  syncNowResultText,
  LAB_LID_LABEL,
  LAB_RID_LABEL,
  laboratoriesOf,
  mappingDraftFrom,
  RESERVE_PATIENT_ID_TEXT,
  QUARANTINE_REPORT_PARAM,
  quarantineReasonLabel,
  reasonCountRows,
  rerunnableCount,
  resolutionReasonError,
  UNKNOWN_ANALYTE_REASON_LABEL,
  unknownAnalyteKeyOf,
  unknownAnalyteRows,
  type UnknownAnalyteRow,
} from "@/lib/labportalen";
import {
  attachLabPortalenQuarantine,
  dismissLabPortalenQuarantine,
  fetchAnalyteMappings,
  fetchLabPortalenQuarantine,
  fetchLabPortalenStatus,
  syncLabPortalenNow,
} from "@/lib/labportalen-api";
import { adminPatientHref, patientHref } from "@/lib/patient-links";
import { MappingForm, MappingsSection, RerunPanel } from "./LabAnalyteMappings";

// TES-442 — "Provsvar utan koppling": the LabPortalen reports the sweep could
// not attach to an order on its own, and the sweep's own state.
//
// A doctor resolves a report: attaches it to an order she picks, or dismisses
// it. Both take a reason, which the API audits. An admin reads the same page
// from Drift (she runs the integration) without the two actions — read-only
// by policy (TES-1040), and the API refuses her writes anyway.
//
// TES-1263 — a doctor maps the unknown analyses here ("Koppla till analys")
// and deactivates a mapping (LabAnalyteMappings.tsx); an admin sees the
// mappings read-only (TES-1040).
//
// TES-1630 — the re-run ("Koppla om provsvaren") is doctor AND admin (Simon
// 2026-10-27): it applies the sweep's automatic matching and picks no patient
// or order by hand, so it has its own flag, canRerun. Attach and dismiss stay
// on canResolve, doctor only.
//
// TES-1265 — "Hämta nu" beside the sync status starts the cron's sweep now.
// Doctor AND admin (a named TES-1040 exception: it only fetches what the lab
// already sent), so it has its own flag; never a coach, never a support view.
//
// The list carries no personal data: the masked personnummer (birth date
// only) is shown in the opened report, not in the table. Every Swedish
// string here is PROPOSED copy awaiting Emelie.

function fmtDateTime(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Stockholm",
  });
}

const ORDER_STATUS_LABEL: Record<string, string> = {
  ordered: "Beställt",
  remiss_sent: "Remiss skickad",
  partial: "Delsvar",
  resulted: "Provsvar inkommet",
};

function failText(e: unknown): string {
  return errorText(e instanceof Error ? e.message : String(e));
}

export function LabPortalenRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const readOnly = useReadOnly();
  const canResolve = canResolveQuarantine(claims?.role, readOnly);
  const canRerun = canRerunQuarantine(claims?.role, claims?.roles, readOnly);
  const canManage = canManageMappings(claims?.role, readOnly);
  const canFetchNow = canFetchLabPortalenNow(
    claims?.role,
    claims?.roles,
    readOnly,
  );
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState<ReturnType<
    typeof syncNowResultText
  > | null>(null);
  const [mappings, setMappings] = useState<LabAnalyteMappingList | null>(null);
  const [mapDraft, setMapDraft] = useState<UnknownAnalyteRow | null>(null);
  const [status, setStatus] = useState<LabPortalenStatus | null>(null);
  const [items, setItems] = useState<LabPortalenQuarantineItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TES-1179 — a lab order's "Svar mottaget – kunde inte läsas in" links
  // here with the report's id; that report opens.
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get(QUARANTINE_REPORT_PARAM);
  const [open, setOpen] = useState<string | null>(linkedId);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, q, m] = await Promise.all([
        fetchLabPortalenStatus(),
        fetchLabPortalenQuarantine(),
        fetchAnalyteMappings(),
      ]);
      setStatus(s);
      setItems(q);
      setMappings(m);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(failText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!linkedId || !items?.some((i) => i.id === linkedId)) return;
    document
      .getElementById(`quarantine-${linkedId}`)
      ?.scrollIntoView({ block: "start" });
  }, [items, linkedId]);

  const resolved = (text: string) => {
    setOpen(null);
    setNotice(text);
    void load();
  };

  const fetchNow = async () => {
    if (fetching) return;
    setFetching(true);
    setFetchResult(null);
    try {
      setFetchResult(syncNowResultText(await syncLabPortalenNow()));
      await load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setFetchResult({ text: failText(e), tone: "error" });
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          Provsvar utan koppling
        </h1>
        <p className="text-sm text-textSecondary max-w-2xl">
          Provsvar från LabPortalen som inte kunde kopplas till en
          provbeställning automatiskt. Läkaren kopplar svaret till rätt
          beställning eller avfärdar det. Båda kräver en orsak och loggas.
        </p>
        {!canResolve ? (
          <p className="text-sm text-textSecondary max-w-2xl">
            Endast läkare kan koppla eller avfärda provsvar. Du ser listan och
            synkens status.
          </p>
        ) : null}
      </div>

      {error ? (
        <div
          role="alert"
          className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm"
        >
          {error}
        </div>
      ) : null}
      {notice ? (
        <div
          role="status"
          className="bg-success/10 border border-success/30 text-success rounded-md p-md text-sm"
        >
          {notice}
        </div>
      ) : null}

      {status ? (
        <StatusPanel
          status={status}
          canFetchNow={canFetchNow}
          fetching={fetching}
          fetchResult={fetchResult}
          onFetchNow={() => void fetchNow()}
          canManage={canManage && mappings !== null}
          onMap={(row) => {
            setNotice(null);
            setMapDraft(row);
          }}
        />
      ) : null}

      {mapDraft && mappings ? (
        <MappingForm
          key={unknownAnalyteKeyOf(mapDraft)}
          initial={mappingDraftFrom(mapDraft)}
          panel={mappings.panel}
          onCancel={() => setMapDraft(null)}
          onDone={() => {
            setMapDraft(null);
            resolved("Kopplingen är sparad.");
          }}
          onUnauthorized={() => void signOutRef.current()}
        />
      ) : null}

      {canRerun && items && rerunnableCount(items) > 0 ? (
        <RerunPanel
          count={rerunnableCount(items)}
          onDone={(r) =>
            resolved(
              `${r.attached} provsvar kopplades. ${r.requarantined.length} väntar på läkaren.`,
            )
          }
          onUnauthorized={() => void signOutRef.current()}
        />
      ) : null}

      {items === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}

      {items && items.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
          Inga provsvar väntar på koppling.
        </div>
      ) : null}

      {items && items.length > 0 ? (
        <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                <th className="px-md py-sm font-medium">Mottaget</th>
                <th className="px-md py-sm font-medium">Orsak</th>
                <th className="px-md py-sm font-medium">Labb</th>
                <th className="px-md py-sm font-medium">Analyser</th>
                <th className="px-md py-sm font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <QuarantineRow
                  key={item.id}
                  item={item}
                  open={open === item.id}
                  canResolve={canResolve}
                  actingRole={claims?.role}
                  onToggle={() =>
                    setOpen((o) => (o === item.id ? null : item.id))
                  }
                  onResolved={resolved}
                  onUnauthorized={() => void signOutRef.current()}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {mappings ? (
        <MappingsSection
          mappings={mappings.mappings}
          canManage={canManage}
          onChanged={resolved}
          onUnauthorized={() => void signOutRef.current()}
        />
      ) : null}
    </div>
  );
}

const FETCH_RESULT_CLASS: Record<"success" | "info" | "error", string> = {
  success: "text-success",
  info: "text-textSecondary",
  error: "text-danger",
};

function StatusPanel({
  status,
  canFetchNow,
  fetching,
  fetchResult,
  onFetchNow,
  canManage,
  onMap,
}: {
  status: LabPortalenStatus;
  canFetchNow: boolean;
  fetching: boolean;
  fetchResult: ReturnType<typeof syncNowResultText> | null;
  onFetchNow: () => void;
  canManage: boolean;
  onMap: (row: UnknownAnalyteRow) => void;
}) {
  const reasons = reasonCountRows(status.openQuarantineByReason);
  const unknown = unknownAnalyteRows(status.unknownAnalytes);
  const nowMapped = new Set(status.unknownAnalytesNowMapped ?? []);
  const cursor = status.cursor;
  return (
    <section
      aria-labelledby="labportalen-status"
      className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md"
    >
      <div className="flex flex-wrap items-center justify-between gap-sm">
        <h2 id="labportalen-status" className="font-medium">
          Synk med LabPortalen
        </h2>
        {canFetchNow ? (
          <button
            type="button"
            onClick={onFetchNow}
            disabled={fetching}
            aria-busy={fetching}
            className="px-md py-sm rounded-md border border-surfaceMuted text-sm disabled:opacity-50"
          >
            {fetchNowLabel(fetching)}
          </button>
        ) : null}
      </div>
      {fetchResult ? (
        <p
          role={fetchResult.tone === "error" ? "alert" : "status"}
          className={`text-sm ${FETCH_RESULT_CLASS[fetchResult.tone]}`}
        >
          {fetchResult.text}
        </p>
      ) : null}
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-md text-sm">
        <div>
          <dt className="text-xs text-textSecondary">Synk</dt>
          <dd>
            {status.syncEnabled ? "På" : "Av"}
            {status.mockMode ? " (testläge)" : ""}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-textSecondary">Senast lyckad</dt>
          <dd>{fmtDateTime(cursor?.lastSuccessAt ?? null)}</dd>
        </div>
        <div>
          <dt className="text-xs text-textSecondary">Senaste körning</dt>
          <dd>{fmtDateTime(cursor?.lastRunAt ?? null)}</dd>
        </div>
        <div>
          <dt className="text-xs text-textSecondary">Läst till och med</dt>
          <dd className="tabular-nums">{cursor ? cursor.highestId : "–"}</dd>
        </div>
      </dl>
      {cursor?.lastError ? (
        <p role="alert" className="text-sm text-danger">
          Senaste fel: {cursor.lastError}
        </p>
      ) : null}

      <div className="flex flex-col gap-xs">
        <h3 className="text-sm font-medium">
          Väntar på koppling: {status.openQuarantine}
        </h3>
        {reasons.length > 0 ? (
          <ul className="text-sm flex flex-col gap-[2px]">
            {reasons.map((r) => (
              <li key={r.reason} className="flex gap-sm">
                <span className="tabular-nums w-8 text-right">{r.count}</span>
                <span>{r.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="flex flex-col gap-xs">
        <h3 className="text-sm font-medium">Analyser som saknar mappning</h3>
        <p className="text-xs text-textSecondary">
          Analyskoder som labben skickat men Ella inte känner igen, så att
          mappningen kan kompletteras. Bara koder och antal, aldrig värden.
        </p>
        {unknown.length === 0 ? (
          <p className="text-sm text-textSecondary">Inga omappade analyser.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                  <th className="pr-md py-xs font-medium">Kod</th>
                  <th className="pr-md py-xs font-medium">Namn</th>
                  <th className="pr-md py-xs font-medium">Enhet</th>
                  <th className="pr-md py-xs font-medium">Varför</th>
                  <th className="py-xs font-medium text-right">Antal</th>
                  <th className="pl-md py-xs font-medium" />
                </tr>
              </thead>
              <tbody>
                {unknown.map((u) => (
                  <tr
                    key={`${u.testMethodCode}|${u.anaName}|${u.unit}|${u.reason}`}
                    className="border-t border-surfaceMuted"
                  >
                    <td className="pr-md py-xs font-mono text-xs">
                      {u.testMethodCode || "–"}
                    </td>
                    <td className="pr-md py-xs">{u.anaName || "–"}</td>
                    <td className="pr-md py-xs">{u.unit || "–"}</td>
                    <td className="pr-md py-xs">
                      {UNKNOWN_ANALYTE_REASON_LABEL[u.reason] ?? u.reason}
                    </td>
                    <td className="py-xs text-right tabular-nums">{u.count}</td>
                    <td className="pl-md py-xs whitespace-nowrap">
                      {nowMapped.has(unknownAnalyteKeyOf(u)) ? (
                        <span className="text-textSecondary">Kopplad</span>
                      ) : canManage ? (
                        <button
                          type="button"
                          onClick={() => onMap(u)}
                          className="text-brand hover:underline"
                        >
                          Koppla till analys
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function QuarantineRow({
  item,
  open,
  canResolve,
  actingRole,
  onToggle,
  onResolved,
  onUnauthorized,
}: {
  item: LabPortalenQuarantineItem;
  open: boolean;
  canResolve: boolean;
  actingRole: string | undefined;
  onToggle: () => void;
  onResolved: (notice: string) => void;
  onUnauthorized: () => void;
}) {
  const labs = laboratoriesOf(item);
  return (
    <>
      <tr
        id={`quarantine-${item.id}`}
        className="border-t border-surfaceMuted align-top"
      >
        <td className="px-md py-sm whitespace-nowrap">
          {fmtDateTime(item.createdAt)}
        </td>
        <td className="px-md py-sm">{quarantineReasonLabel(item.reason)}</td>
        <td className="px-md py-sm">{labs.length ? labs.join(", ") : "–"}</td>
        <td className="px-md py-sm tabular-nums">{item.analyteCount}</td>
        <td className="px-md py-sm whitespace-nowrap">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="text-brand hover:underline"
          >
            {open ? "Stäng" : "Visa"}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="bg-background">
          <td colSpan={5} className="px-md py-md">
            <QuarantineDetail
              item={item}
              canResolve={canResolve}
              actingRole={actingRole}
              onResolved={onResolved}
              onUnauthorized={onUnauthorized}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

type Action = "none" | "attach" | "dismiss";

function QuarantineDetail({
  item,
  canResolve,
  actingRole,
  onResolved,
  onUnauthorized,
}: {
  item: LabPortalenQuarantineItem;
  canResolve: boolean;
  actingRole: string | undefined;
  onResolved: (notice: string) => void;
  onUnauthorized: () => void;
}) {
  const [action, setAction] = useState<Action>("none");
  const showValueReason = item.analyses.some((a) => a.unmappedReason);
  return (
    <div className="flex flex-col gap-md">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-md text-sm">
        <div>
          <dt className="text-xs text-textSecondary">Orsak</dt>
          <dd>{quarantineReasonLabel(item.reason)}</dd>
        </div>
        <div>
          <dt className="text-xs text-textSecondary">Svarstid enligt labbet</dt>
          <dd>{fmtDateTime(item.reportTime)}</dd>
        </div>
        {/* TES-1341 — RequisitionID is the lab's number (LID), and
            ExternalRequisitionID the remiss id (RID). */}
        <div>
          <dt className="text-xs text-textSecondary">{LAB_LID_LABEL}</dt>
          <dd className="font-mono text-xs">{item.requisitionId ?? "–"}</dd>
        </div>
        <div>
          <dt className="text-xs text-textSecondary">{LAB_RID_LABEL}</dt>
          <dd className="font-mono text-xs">
            {item.externalRequisitionId ?? "–"}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-textSecondary">Personnummer</dt>
          <dd>
            {item.personalNumberMasked ??
              (item.hasReservePatientId
                ? RESERVE_PATIENT_ID_TEXT
                : "Saknas eller reservnummer")}
          </dd>
        </div>
      </dl>

      {item.attachments && item.attachments.length > 0 ? (
        <p className="text-sm" data-testid="quarantine-attachments">
          <span className="font-semibold">
            {LAB_ATTACHMENT_IN_LABPORTALEN_LABEL}
          </span>
          {item.attachments.some((a) => a.name)
            ? `: ${item.attachments
                .map((a) => a.name)
                .filter(Boolean)
                .join(", ")}`
            : null}
        </p>
      ) : null}

      <div className="text-sm">
        {item.matchedPatientId ? (
          <>
            Personnumret hör till en patient hos Ella.{" "}
            <Link
              to={adminPatientHref(actingRole, item.matchedPatientId)}
              className="text-brand hover:underline"
            >
              Öppna patienten
            </Link>
          </>
        ) : (
          <span className="text-textSecondary">
            Personnumret hör inte till någon patient hos Ella.
          </span>
        )}
      </div>

      {item.analyses.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                <th className="pr-md py-xs font-medium">Kod</th>
                <th className="pr-md py-xs font-medium">Analys</th>
                <th className="pr-md py-xs font-medium">Resultat</th>
                <th className="py-xs font-medium">Enhet</th>
                {/* TES-1617 — a row holding the values an attached report
                    could not store: why each one was not. */}
                {showValueReason ? (
                  <th className="pl-md py-xs font-medium">Orsak</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {item.analyses.map((a, i) => (
                <tr key={i} className="border-t border-surfaceMuted">
                  <td className="pr-md py-xs font-mono text-xs">
                    {a.testMethodCode ?? "–"}
                  </td>
                  <td className="pr-md py-xs">{a.anaName ?? "–"}</td>
                  <td className="pr-md py-xs tabular-nums">
                    {analysisValueText(a)}
                  </td>
                  <td className="py-xs">{a.unit ?? "–"}</td>
                  {showValueReason ? (
                    <td className="pl-md py-xs">
                      {a.unmappedReason
                        ? (UNKNOWN_ANALYTE_REASON_LABEL[a.unmappedReason] ??
                          a.unmappedReason)
                        : "–"}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-textSecondary">
          Svaret innehåller inga analyser som kunde läsas.
        </p>
      )}

      {canResolve ? (
        <div className="flex flex-col gap-sm">
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={() => setAction("attach")}
              aria-pressed={action === "attach"}
              disabled={item.analyses.length === 0}
              className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
            >
              Koppla till beställning
            </button>
            <button
              type="button"
              onClick={() => setAction("dismiss")}
              aria-pressed={action === "dismiss"}
              className="px-md py-sm rounded-md border border-surfaceMuted text-sm"
            >
              Avfärda
            </button>
          </div>
          {action === "attach" ? (
            <AttachForm
              item={item}
              onDone={() =>
                onResolved("Provsvaret är kopplat till beställningen.")
              }
              onUnauthorized={onUnauthorized}
            />
          ) : null}
          {action === "dismiss" ? (
            <DismissForm
              item={item}
              onDone={() => onResolved("Provsvaret är avfärdat.")}
              onUnauthorized={onUnauthorized}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ReasonField({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <label className="flex flex-col gap-xs text-sm">
      {label}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        rows={2}
        required
        className="rounded-md border border-surfaceMuted bg-surface p-sm text-sm"
      />
      <span className="text-xs text-textSecondary">
        Sparas i åtkomstloggen.
      </span>
    </label>
  );
}

type PickedPatient = {
  id: string;
  name: string | null;
  orders: ClinicianLabOrder[];
};

function AttachForm({
  item,
  onDone,
  onUnauthorized,
}: {
  item: LabPortalenQuarantineItem;
  onDone: () => void;
  onUnauthorized: () => void;
}) {
  const [patient, setPatient] = useState<PickedPatient | null>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<PatientListRow[] | null>(null);
  const [orderId, setOrderId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(failText(e));
    },
    [onUnauthorized],
  );

  const pick = useCallback(
    async (patientId: string) => {
      setError(null);
      setLoadingPatient(true);
      try {
        const detail = await fetchPatient(patientId);
        const orders = attachableOrders(detail.labOrders);
        setPatient({ id: patientId, name: detail.patient.name, orders });
        setOrderId(orders.length === 1 ? orders[0].id : "");
      } catch (e) {
        fail(e);
      } finally {
        setLoadingPatient(false);
      }
    },
    [fail],
  );

  // The patient the personnummer resolves to is the likely answer; start
  // there, and let the doctor search for someone else.
  useEffect(() => {
    if (item.matchedPatientId) void pick(item.matchedPatientId);
  }, [item.matchedPatientId, pick]);

  async function search() {
    setError(null);
    try {
      setHits((await fetchPatients(query)).rows);
    } catch (e) {
      fail(e);
    }
  }

  const reasonError = resolutionReasonError(reason);

  async function submit() {
    if (!orderId || reasonError || busy) return;
    setBusy(true);
    setError(null);
    try {
      await attachLabPortalenQuarantine(item.id, orderId, reason);
      onDone();
    } catch (e) {
      fail(e);
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-sm max-w-2xl border border-surfaceMuted rounded-md p-md bg-surface"
    >
      <div className="flex flex-col gap-xs">
        <span className="text-sm font-medium">Patient</span>
        {loadingPatient ? (
          <p className="text-sm text-textSecondary">Hämtar patienten…</p>
        ) : patient ? (
          <p className="text-sm">
            {patient.name ?? "Namn saknas"}{" "}
            <Link
              to={patientHref(patient.id, "blodprov")}
              className="text-brand hover:underline"
            >
              Blodprov
            </Link>
          </p>
        ) : (
          <p className="text-sm text-textSecondary">Ingen patient vald.</p>
        )}
        <div className="flex gap-sm items-end">
          <label className="flex flex-col gap-xs text-xs text-textSecondary">
            Sök annan patient på personnummer
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputMode="numeric"
              className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
            />
          </label>
          <button
            type="button"
            onClick={() => void search()}
            disabled={query.trim().length < 4}
            className="px-md py-xs rounded-md border border-surfaceMuted text-sm disabled:opacity-50"
          >
            Sök
          </button>
        </div>
        {hits && hits.length === 0 ? (
          <p className="text-sm text-textSecondary">Ingen patient hittades.</p>
        ) : null}
        {hits && hits.length > 0 ? (
          <ul className="text-sm flex flex-col gap-[2px]">
            {hits.slice(0, 10).map((h) => (
              <li key={h.patientId}>
                <button
                  type="button"
                  onClick={() => {
                    setHits(null);
                    void pick(h.patientId);
                  }}
                  className="text-brand hover:underline"
                >
                  {h.name ?? "Namn saknas"} (…{h.personalNumberLast6})
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {patient ? (
        <fieldset className="flex flex-col gap-xs">
          <legend className="text-sm font-medium">Provbeställning</legend>
          {patient.orders.length === 0 ? (
            <p className="text-sm text-textSecondary">
              Patienten har ingen provbeställning som kan ta emot svaret.
            </p>
          ) : (
            patient.orders.map((o) => (
              <label key={o.id} className="flex items-center gap-sm text-sm">
                <input
                  type="radio"
                  name={`order-${item.id}`}
                  value={o.id}
                  checked={orderId === o.id}
                  onChange={() => setOrderId(o.id)}
                />
                Beställd {fmtDateTime(o.orderedAt)} –{" "}
                {ORDER_STATUS_LABEL[o.status] ?? o.status}
                {o.requisitionRef ? ` – remiss ${o.requisitionRef}` : ""}
              </label>
            ))
          )}
        </fieldset>
      ) : null}

      <ReasonField
        value={reason}
        onChange={setReason}
        label="Varför hör svaret till den här beställningen?"
      />
      {reason && reasonError ? (
        <p className="text-xs text-danger">{reasonError}</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <button
          type="submit"
          disabled={!orderId || !!reasonError || busy}
          className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Kopplar…" : "Koppla provsvaret"}
        </button>
      </div>
    </form>
  );
}

function DismissForm({
  item,
  onDone,
  onUnauthorized,
}: {
  item: LabPortalenQuarantineItem;
  onDone: () => void;
  onUnauthorized: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonError = resolutionReasonError(reason);

  async function submit() {
    if (reasonError || busy) return;
    setBusy(true);
    setError(null);
    try {
      await dismissLabPortalenQuarantine(item.id, reason);
      onDone();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(failText(e));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-sm max-w-2xl border border-surfaceMuted rounded-md p-md bg-surface"
    >
      <p className="text-sm text-textSecondary">
        Ett avfärdat svar kopplas inte till någon patient. Personnumret och
        analyserna raderas från karantänen.
      </p>
      <ReasonField
        value={reason}
        onChange={setReason}
        label="Varför avfärdas svaret?"
      />
      {reason && reasonError ? (
        <p className="text-xs text-danger">{reasonError}</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <button
          type="submit"
          disabled={!!reasonError || busy}
          className="px-md py-sm rounded-md bg-danger text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Avfärdar…" : "Avfärda provsvaret"}
        </button>
      </div>
    </form>
  );
}
